import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Lock, AlertCircle, Eye, EyeOff, CheckCircle, User } from 'lucide-react';
import { authAPI } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * AuthModal - Login/Register/Forgot Password
 * With Google OAuth support
 */
function AuthModal({ isOpen, onClose, onLogin }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // ==========================================
  // GOOGLE SIGN-IN SETUP
  // ==========================================
  const handleGoogleResponse = useCallback(async (response) => {
    if (!response.credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }

    setGoogleLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: response.credential,
          client_id: GOOGLE_CLIENT_ID
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Google authentication failed');
      }

      const data = await res.json();
      const token = data.access_token;

      if (token) {
        localStorage.setItem('token', token);
        window.location.reload();
      } else {
        throw new Error('No token received');
      }
    } catch (err) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen || !GOOGLE_CLIENT_ID) return;

    // Load Google Identity Services script
    const loadGoogleScript = () => {
      if (document.getElementById('google-gsi-script')) {
        initializeGoogle();
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogle;
      document.head.appendChild(script);
    };

    const initializeGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        // Render button in the container
        const container = document.getElementById('google-signin-btn');
        if (container) {
          container.innerHTML = ''; // Clear previous
          window.google.accounts.id.renderButton(container, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            width: 280,
            text: mode === 'register' ? 'signup_with' : 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
          });
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(loadGoogleScript, 100);
    return () => clearTimeout(timer);
  }, [isOpen, mode, handleGoogleResponse]);

  if (!isOpen) return null;

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setNickname('');
    setError('');
    setShowPassword(false);
  };

  const handleClose = () => {
    resetForm();
    setMode('login');
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const response = await authAPI.login({ email, password });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Login failed');
        }

        const data = await response.json();
        const token = data.access_token;

        if (token) {
          localStorage.setItem('token', token);
          window.location.reload();
        } else {
          throw new Error('No token received');
        }
      } else if (mode === 'register') {
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        if (password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (nickname.length < 2) {
          setError('Nickname must be at least 2 characters');
          setLoading(false);
          return;
        }

        if (!acceptedTerms) {
          setError('You must accept the Terms and Privacy Policy');
          setLoading(false);
          return;
        }

        const response = await authAPI.register({ email, password, nickname, terms_accepted: acceptedTerms });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Registration failed');
        }

        setMode('verify-sent');
        setLoading(false);
        return;
      } else if (mode === 'forgot') {
        await fetch(`${API_URL}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        setMode('forgot-sent');
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Auth error:', err);
      const errorMsg = err.message || 'Authentication failed';
      setError(errorMsg);

      if (errorMsg.toLowerCase().includes('verify')) {
        setMode('verify-sent');
      }
      setLoading(false);
    }
  };

  // Email sent confirmation (forgot password)
  if (mode === 'forgot-sent') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl p-5 max-w-xs w-full relative">
          <button onClick={handleClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="text-green-600" size={24} />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Check Your Email</h2>
            <p className="text-gray-600 text-sm mb-3">
              If an account exists for <strong>{email}</strong>, you'll receive a reset link.
            </p>
            <p className="text-xs text-gray-400">Check your spam folder if you don't see it.</p>
          </div>
        </div>
      </div>
    );
  }

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
    } catch (err) {}
    finally { setLoading(false); }
  };

  // Verification email sent (after register)
  if (mode === 'verify-sent') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl p-5 max-w-xs w-full relative">
          <button onClick={handleClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail className="text-blue-600" size={24} />
            </div>
            <h2 className="text-lg font-bold text-gray-900 mb-2">Verify Your Email</h2>
            <p className="text-gray-600 text-sm mb-3">
              We sent a verification link to <strong>{email}</strong>
            </p>
            <button
              onClick={handleResendVerification}
              disabled={loading}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Resend verification email'}
            </button>
            <div className="border-t border-gray-100 mt-3 pt-2">
              <p className="text-xs text-gray-400">Didn't receive it? Check your spam folder.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl p-5 max-w-xs w-full relative">
        {/* Close Button */}
        <button onClick={handleClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <X size={18} />
        </button>

        {/* Logo */}
        <div className="text-center mb-4">
          <img src="/logo-header.png" alt="MidoLABS" className="h-7 mx-auto mb-2" />
          <h2 className="text-lg font-bold" style={{ color: '#1E3A5F' }}>
            {mode === 'login' ? 'Welcome Back' : mode === 'register' ? 'Create Account' : 'Reset Password'}
          </h2>
        </div>

        {/* Google Sign-In Button */}
        {mode !== 'forgot' && GOOGLE_CLIENT_ID && (
          <>
            <div className="flex justify-center mb-3">
              {googleLoading ? (
                <div className="flex items-center justify-center py-2.5 px-4 border border-gray-200 rounded-lg w-full max-w-[280px]">
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mr-2" />
                  <span className="text-sm text-gray-500">Connecting to Google...</span>
                </div>
              ) : (
                <div id="google-signin-btn" className="flex justify-center" />
              )}
            </div>

            {/* Divider */}
            <div className="relative mb-3">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">or continue with email</span>
              </div>
            </div>
          </>
        )}

        {/* Error */}
        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={14} />
            <span className="text-red-700 text-xs">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                required
              />
            </div>
          </div>

          {/* Nickname (register only) */}
          {mode === 'register' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nickname</label>
              <div className="relative">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="Display name"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                  required
                  minLength={2}
                  maxLength={30}
                />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Shown publicly</p>
            </div>
          )}

          {/* Password */}
          {mode !== 'forgot' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                  required
                  minLength={mode === 'register' ? 8 : 1}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {mode === 'register' && (
                <p className="text-xs text-gray-400 mt-0.5">Min 8 characters</p>
              )}
            </div>
          )}

          {/* Confirm Password */}
          {mode === 'register' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:border-blue-400 focus:ring-1 focus:ring-blue-100 outline-none"
                  required
                />
              </div>
            </div>
          )}

          {/* Forgot Password Link */}
          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); }}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                Forgot password?
              </button>
            </div>
          )}

          {/* Terms Checkbox - Register only */}
          {mode === 'register' && (
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-0.5"
                required
              />
              <span className="text-gray-600">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Terms</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>
              </span>
            </label>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm"
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                {mode === 'forgot' ? 'Sending...' : 'Processing...'}
              </span>
            ) : (
              mode === 'login' ? 'Sign In' : mode === 'register' ? 'Create Account' : 'Send Reset Link'
            )}
          </button>
        </form>

        {/* Toggle between modes */}
        <div className="mt-3 text-center text-xs">
          {mode === 'login' && (
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button onClick={() => { setMode('register'); resetForm(); }} className="text-blue-600 hover:text-blue-700 font-medium">
                Sign up
              </button>
            </p>
          )}
          {mode === 'register' && (
            <p className="text-gray-600">
              Already have an account?{' '}
              <button onClick={() => { setMode('login'); resetForm(); }} className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </button>
            </p>
          )}
          {mode === 'forgot' && (
            <p className="text-gray-600">
              Remember your password?{' '}
              <button onClick={() => { setMode('login'); resetForm(); }} className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthModal;